import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../features/authentication/data-access/auth.service';
import { NotificationService } from '../services/common/notification.service';
import { AUTH_ENDPOINTS } from '../end-points/auth/auth.endpoints';
import { isEndpointRequest } from '../utils/url-match.util';

const RETRY_HEADER = 'X-Auth-Retry';

/** HBW-044: Identity / Master Admin refuse (403) every call but change-password while the user must choose a new one. */
const isPasswordChangeRequired = (error: HttpErrorResponse): boolean =>
  error.status === 403 && (error.error as { errorCode?: string } | null)?.errorCode === 'password_change_required';

// Several requests can hit the forced-logout path at once; only toast once.
let sessionExpiredNotified = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const notification = inject(NotificationService);

  const isLoginRequest = isEndpointRequest(
    req.url,
    AUTH_ENDPOINTS.login
  );

  const isRefreshRequest = isEndpointRequest(
    req.url,
    AUTH_ENDPOINTS.refresh
  );

  // Signing out carries the access token, but a 401 on it must not refresh the session it is ending.
  const isLogoutRequest = isEndpointRequest(req.url, AUTH_ENDPOINTS.logout);

  if (isLoginRequest || isRefreshRequest) {
    return next(req);
  }

  const token = authService.accessToken;
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (isPasswordChangeRequired(error)) {
        authService.markPasswordChangeRequired();
        if (!router.url.startsWith('/auth/change-password')) {
          void router.navigateByUrl('/auth/change-password');
        }
        return throwError(() => error);
      }

      if (error.status !== 401 || !authService.accessToken || isRefreshRequest || isLogoutRequest || req.headers.has(RETRY_HEADER)) {
        return throwError(() => error);
      }

      return authService.refreshAccessToken().pipe(
        switchMap(() => {
          const refreshedToken = authService.accessToken;

          if (!refreshedToken) {
            return throwError(() => error);
          }

          const retriedRequest = req.clone({
            setHeaders: {
              Authorization: `Bearer ${refreshedToken}`,
              [RETRY_HEADER]: 'true',
            },
          });

          return next(retriedRequest);
        }),
        catchError((refreshError) => {
          if (!sessionExpiredNotified) {
            sessionExpiredNotified = true;
            setTimeout(() => (sessionExpiredNotified = false), 5000);
            notification.error('Session expired. Please sign in again.');
          }
          authService.logout();
          const currentUrl = router.url;
          const safeReturnUrl = currentUrl.startsWith('/') ? currentUrl : '/';
          void router.navigate(['/auth/login'], {
            queryParams: { returnUrl: safeReturnUrl },
          });
          return throwError(() => refreshError);
        })
      );
    })
  );
};
